<script setup>
import { computed, ref, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import DomcrmAPI from 'dashboard/api/domcrm';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
  isAccordionOpen: {
    type: Boolean,
    default: false,
  },
});

// State
const domCrmResult = ref(null);
const domCrmError = ref('');
const userCrmResult = ref(null);
const userCrmError = ref('');
const createLeadUrl = ref('');
const loading = ref(false);
const userLoading = ref(false);
const consultado = ref(false);

// Getters
const contactGetter = useMapGetter('contacts/getContact');
const contactDetails = computed(() => contactGetter.value(props.contactId));

// Computed
const contactFullName = computed(() => {
  if (!domCrmResult.value) return 'Contato DOM CRM';
  return getFullName(domCrmResult.value) || 'Nome não disponível';
});

const responsavelFullName = computed(() => {
  return getFullName(userCrmResult.value) || 'Não definido';
});

const isLead = computed(() => {
  if (!domCrmResult.value) return false;
  const mod = (
    domCrmResult.value.modulo ||
    domCrmResult.value.module ||
    ''
  ).toLowerCase();
  return mod.includes('lead');
});

const isOrganization = computed(() => {
  if (!domCrmResult.value) return false;
  const mod = (
    domCrmResult.value.modulo ||
    domCrmResult.value.module ||
    ''
  ).toLowerCase();
  return mod.includes('organiza');
});

const crmUrl = computed(() => domCrmResult.value?.url || null);

const hasResponsavelId = computed(
  () =>
    !!(
      domCrmResult.value?.userid ||
      domCrmResult.value?.assigned_user_id ||
      domCrmResult.value?.user_id
    )
);

const isUsuarioNaoEncontrado = computed(() =>
  domCrmError.value.includes('não encontrado')
);

const moduloLabel = computed(() => {
  if (!domCrmResult.value) return 'Contato';
  return domCrmResult.value.modulo || domCrmResult.value.module || 'Contato';
});

const leadStatusLabel = computed(() => {
  if (!domCrmResult.value) return 'N/A';
  return (
    domCrmResult.value.leadstatus || domCrmResult.value.lead_status || 'N/A'
  );
});

// Methods
function getFullName(data) {
  if (!data) return '';
  const firstName = data.firstname || data.first_name || '';
  const lastName = data.lastname || data.last_name || '';
  const name = data.name || '';

  if (firstName && lastName) return `${firstName} ${lastName}`.trim();
  if (firstName) return firstName;
  if (lastName) return lastName;
  if (name) return name;
  return '';
}

function resetState() {
  domCrmResult.value = null;
  domCrmError.value = '';
  userCrmResult.value = null;
  userCrmError.value = '';
  createLeadUrl.value = '';
  consultado.value = false;
}

function resolveCreateLeadUrl(meta) {
  return meta?.create_lead_url || meta?.createLeadUrl || '';
}

function splitFullName(name) {
  const normalizedName = (name || '').trim();

  if (!normalizedName) {
    return { firstName: '', lastName: '' };
  }

  const [firstName, ...rest] = normalizedName.split(/\s+/);
  return {
    firstName,
    lastName: rest.join(' ').trim(),
  };
}

function normalizePhone(phone) {
  return (phone || '').replace(/\D/g, '');
}

// Remove o DDI do Brasil (55) de um telefone já normalizado (só dígitos).
// Números brasileiros com DDI têm sempre 12 (DDD 2 + fixo 8) ou 13
// (DDD 2 + celular 9) dígitos. Um número nacional puro (sem DDI) nunca
// passa de 11 dígitos, mesmo com DDD 55 real (ex: Santa Maria/RS) — então
// tamanho 12/13 + prefixo "55" só pode ser DDI, nunca DDD.
function stripBrazilCountryCode(phone) {
  const normalized = phone || '';
  const hasBrazilCountryCode =
    (normalized.length === 12 || normalized.length === 13) &&
    normalized.startsWith('55');
  return hasBrazilCountryCode ? normalized.slice(2) : normalized;
}

function leadPrefillParams() {
  const contact = contactDetails.value || {};
  const additionalAttributes = contact.additional_attributes || {};
  const socialProfiles = additionalAttributes.social_profiles || {};
  const { firstName, lastName } = splitFullName(contact.name);
  const phone = normalizePhone(contact.phone_number) || contact.phone_number || '';

  return {
    firstname: firstName,
    lastname: lastName,
    email: contact.email || '',
    mobile: stripBrazilCountryCode(phone),
    company: additionalAttributes.company_name || '',
    city: additionalAttributes.city || '',
    country: additionalAttributes.country || '',
    description: additionalAttributes.description || '',
    identifier: contact.identifier || '',
    telegram: additionalAttributes.social_telegram_user_name || socialProfiles.telegram || '',
    twitter: additionalAttributes.screen_name || socialProfiles.twitter || '',
  };
}

function buildCreateLeadUrlWithPrefill(baseUrl) {
  try {
    const url = new URL(baseUrl);
    const params = leadPrefillParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        url.searchParams.set(key, value);
      }
    });

    return url.toString();
  } catch (_) {
    return baseUrl;
  }
}

async function consultarDomCrm() {
  domCrmError.value = '';
  domCrmResult.value = null;
  userCrmResult.value = null;
  userCrmError.value = '';
  createLeadUrl.value = '';
  loading.value = true;
  consultado.value = true;

  if (!contactDetails.value?.phone_number) {
    domCrmError.value = 'Contato não possui número de telefone';
    loading.value = false;
    return;
  }

  try {
    const { data, meta } = await DomcrmAPI.getIdByPhone(
      contactDetails.value.phone_number
    );
    createLeadUrl.value = resolveCreateLeadUrl(meta);
    domCrmResult.value = data;

    // Buscar dados do responsável se houver ID
    const responsavelId =
      data?.userid || data?.assigned_user_id || data?.user_id;

    if (responsavelId) {
      userLoading.value = true;
      try {
        const { data: userResult } = await DomcrmAPI.getUserById(responsavelId);
        userCrmResult.value = userResult;
      } catch (userErr) {
        userCrmError.value =
          userErr.message || 'Erro ao consultar dados do responsável';
      } finally {
        userLoading.value = false;
      }
    }
  } catch (e) {
    createLeadUrl.value = resolveCreateLeadUrl(e.meta);
    domCrmError.value = e.message || 'Erro ao consultar DOM 360 CRM';
  } finally {
    loading.value = false;
  }
}

function criarLead() {
  if (!createLeadUrl.value) {
    return;
  }

  const urlWithPrefill = buildCreateLeadUrlWithPrefill(createLeadUrl.value);
  window.open(urlWithPrefill, '_blank', 'noopener,noreferrer');
}

// Watchers
watch(
  () => props.contactId,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      resetState();
      if (props.isAccordionOpen && contactDetails.value?.phone_number) {
        consultarDomCrm();
      }
    }
  }
);

watch(
  () => props.isAccordionOpen,
  isOpen => {
    if (isOpen && !consultado.value && contactDetails.value?.phone_number) {
      consultarDomCrm();
    }
  }
);
</script>

<template>
  <div class="p-2">
    <!-- Header com botão atualizar -->
    <div class="flex items-center justify-between mb-2">
      <h6 class="m-0 text-xs font-medium text-n-slate-12">
        {{
          consultado ? 'Informações do DOM 360 CRM:' : 'Consultar DOM 360 CRM'
        }}
      </h6>
      <NextButton
        v-tooltip.left="'Atualizar dados do CRM'"
        slate
        ghost
        xs
        icon="i-lucide-refresh-cw"
        :is-loading="loading"
        @click="consultarDomCrm"
      />
    </div>

    <!-- Estado: Carregando -->
    <div
      v-if="loading"
      class="flex items-center justify-center gap-2 py-4 text-n-slate-11"
    >
      <Spinner :size="18" />
      <span class="text-xs">Consultando dados...</span>
    </div>

    <!-- Estado: Erro -->
    <div v-else-if="domCrmError" class="rounded-lg bg-n-ruby-3 p-3 mb-2">
      <p class="m-0 text-xs text-n-ruby-11">
        {{ domCrmError }}
      </p>
      <NextButton
        v-if="isUsuarioNaoEncontrado && createLeadUrl"
        class="mt-2"
        label="Criar Lead no CRM"
        icon="i-lucide-plus"
        blue
        faded
        sm
        @click="criarLead"
      />
    </div>

    <!-- Estado: Sem dados após consulta -->
    <div
      v-else-if="consultado && !domCrmResult"
      class="rounded-lg bg-n-slate-2 p-3 mb-2"
    >
      <p class="m-0 text-xs text-n-slate-11">
        Nenhuma informação encontrada no DOM 360 CRM.
      </p>
      <NextButton
        v-if="createLeadUrl"
        class="mt-2"
        label="Criar Lead no CRM"
        icon="i-lucide-plus"
        blue
        faded
        sm
        @click="criarLead"
      />
    </div>

    <!-- Estado: Dados encontrados -->
    <div
      v-else-if="domCrmResult"
      class="rounded-lg outline outline-1 outline-n-weak overflow-hidden mb-2"
    >
      <!-- Nome completo -->
      <div
        class="flex items-center justify-between px-3 py-2 text-xs bg-n-slate-2"
      >
        <span class="font-medium text-n-slate-12">Nome</span>
        <span class="text-n-slate-11 text-right truncate ml-2">
          {{ contactFullName }}
        </span>
      </div>

      <!-- Módulo (Tipo) -->
      <div class="flex items-center justify-between px-3 py-2 text-xs">
        <span class="font-medium text-n-slate-12">Tipo</span>
        <span class="text-n-slate-11 capitalize inline-flex items-center gap-1">
          <span
            class="inline-block w-1.5 h-1.5 rounded-full"
            :class="isLead ? 'bg-n-amber-9' : isOrganization ? 'bg-n-violet-9' : 'bg-n-teal-9'"
          />
          {{ moduloLabel }}
        </span>
      </div>

      <!-- Status do Lead (apenas se for lead) -->
      <div
        v-if="isLead"
        class="flex items-center justify-between px-3 py-2 text-xs bg-n-slate-2"
      >
        <span class="font-medium text-n-slate-12">Status</span>
        <span class="text-n-slate-11">{{ leadStatusLabel }}</span>
      </div>

      <!-- Responsável -->
      <div
        class="flex items-center justify-between px-3 py-2 text-xs"
        :class="{ 'bg-n-slate-2': !isLead }"
      >
        <span class="font-medium text-n-slate-12">Responsável</span>
        <div class="flex items-center gap-1">
          <template v-if="userCrmResult">
            <span class="text-n-blue-11 font-medium">
              {{ responsavelFullName }}
            </span>
          </template>
          <template v-else-if="userLoading">
            <Spinner :size="12" class="text-n-slate-10" />
            <span class="text-n-slate-10 text-xs">Carregando...</span>
          </template>
          <template v-else-if="hasResponsavelId">
            <span class="text-n-amber-11 text-xs">Erro ao carregar</span>
          </template>
          <template v-else>
            <span class="text-n-slate-10">Não atribuído</span>
          </template>
        </div>
      </div>

      <!-- Link para o CRM -->
      <div
        v-if="crmUrl"
        class="px-3 py-2 text-xs border-t border-n-weak bg-n-slate-2"
      >
        <a
          :href="crmUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1.5 text-n-blue-11 hover:underline text-xs"
        >
          <span class="i-lucide-external-link w-3.5 h-3.5" />
          Abrir no DOM 360 CRM
        </a>
      </div>

      <!-- Erro ao carregar responsável (sutil) -->
      <div
        v-if="userCrmError"
        class="px-3 py-1.5 text-xs text-n-amber-11 bg-n-amber-3 border-t border-n-weak"
      >
        Responsável: {{ userCrmError }}
      </div>
    </div>

    <!-- Estado: Não consultado ainda -->
    <div v-else class="flex flex-col items-center gap-2 py-3">
      <p class="m-0 text-xs text-n-slate-10 text-center">
        Consulte informações do contato no CRM.
      </p>
      <NextButton
        label="Consultar dados"
        icon="i-lucide-search"
        slate
        faded
        sm
        @click="consultarDomCrm"
      />
    </div>
  </div>
</template>
